/**
 * Utility Bills — Accounts → Utility Bills sub-page.
 *
 * Features
 *   • Add bill: Service Provider, Utility Type, dependent "Paid For"
 *     dropdown (swap locations / staff accommodation / staff / rider
 *     mobile), Billing Month/Year, Amount, optional PDF upload.
 *   • Interactive log: row click opens full details dialog.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from './ui/table';
import { Plus, FileText, Loader2, Trash2, ExternalLink, Zap, MoreVertical, Pencil } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
});

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const monthLabel = (m) => MONTHS[(Number(m) || 1) - 1] || '—';

const PROVIDERS = ['DEWA', 'SEWA', 'e&', 'Du', 'LPG', 'SALIK'];
const UTILITY_TYPES = ['Electricity & Water', 'Internet & Landline', 'Mobile', 'SALIK Top-up'];

const UtilityBills = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState({ count: 0, amount: 0 });
  const [options, setOptions] = useState({ swap_locations: [], staff: [] });
  const [addOpen, setAddOpen] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [detailsBill, setDetailsBill] = useState(null);
  // Filter state
  const [fProvider, setFProvider] = useState('');
  const [fType, setFType] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [fYear, setFYear] = useState('');
  const [fPaidFor, setFPaidFor] = useState('');

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fProvider) params.set('service_provider', fProvider);
      if (fType) params.set('utility_type', fType);
      if (fMonth) params.set('billing_month', fMonth);
      if (fYear) params.set('billing_year', fYear);
      if (fPaidFor) params.set('paid_for_id', fPaidFor);
      const res = await axios.get(`${API}/utility-bills?${params}`, getAuth());
      setBills(res.data?.bills || []);
      setTotal({ count: res.data?.total || 0, amount: res.data?.total_amount || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load utility bills');
    } finally {
      setLoading(false);
    }
  }, [fProvider, fType, fMonth, fYear, fPaidFor]);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/utility-bills/options`, getAuth());
      setOptions({
        swap_locations: res.data?.swap_locations || [],
        staff: res.data?.staff || [],
      });
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchBills(); fetchOptions(); }, [fetchBills, fetchOptions]);

  const downloadMonthlyReport = async () => {
    if (!fMonth || !fYear) {
      toast.error('Pick a Month and Year first to generate the monthly report.');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.set('billing_month', fMonth);
      params.set('billing_year', fYear);
      if (fProvider) params.set('service_provider', fProvider);
      if (fType) params.set('utility_type', fType);
      const res = await axios.get(`${API}/utility-bills/report?${params}`, {
        ...getAuth(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `utility-bills-${fYear}-${String(fMonth).padStart(2,'0')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Monthly report downloaded');
    } catch (e) {
      toast.error('Report download failed');
    }
  };

  const deleteBill = async (id) => {
    if (!window.confirm('Delete this utility bill? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/utility-bills/${id}`, getAuth());
      toast.success('Bill deleted');
      setDetailsBill(null);
      fetchBills();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Utility Bills
          </h2>
          <p className="text-xs text-slate-500">
            DEWA, SEWA, e&amp;, Du, LPG — track every utility bill paid by the company.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Total Paid (shown)</p>
            <p className="text-lg font-bold text-slate-800">AED {Number(total.amount || 0).toLocaleString()}</p>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="utility-bills-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Utility Bill
          </Button>
        </div>
      </div>

      {/* Filter strip */}
      <Card>
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Service Provider</Label>
            <Select value={fProvider || 'all'} onValueChange={(v) => setFProvider(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="filter-provider"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Utility Type</Label>
            <Select value={fType || 'all'} onValueChange={(v) => setFType(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="filter-type"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {UTILITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Month</Label>
            <Select value={fMonth || 'all'} onValueChange={(v) => setFMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="filter-month"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Year</Label>
            <Select value={fYear || 'all'} onValueChange={(v) => setFYear(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="filter-year"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {YEAR_OPTIONS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Location (Paid For)</Label>
            <Select value={fPaidFor || 'all'} onValueChange={(v) => setFPaidFor(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="filter-location"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {(options.swap_locations || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setFProvider(''); setFType(''); setFMonth(''); setFYear(''); setFPaidFor(''); }} data-testid="filter-clear">
              Clear
            </Button>
            <Button onClick={downloadMonthlyReport} data-testid="download-report-btn" title="Pick Month + Year then click to download CSV report">
              Report
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Bills Log</CardTitle>
          <CardDescription className="text-xs">
            {total.count} bill{total.count !== 1 ? 's' : ''} — click any row to see full details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Billing Period</TableHead>
                <TableHead>Utility Type</TableHead>
                <TableHead>Service Provider</TableHead>
                <TableHead>Paid For</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Paid Through</TableHead>
                <TableHead className="text-center">View Bill</TableHead>
                <TableHead className="text-center">View Receipt</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="text-center w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">
                  <Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading…
                </TableCell></TableRow>
              ) : bills.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">
                  No utility bills logged yet. Click <strong>Add Utility Bill</strong> to record the first one.
                </TableCell></TableRow>
              ) : bills.map((b) => (
                <TableRow
                  key={b.id}
                  className="cursor-pointer hover:bg-slate-50 text-sm"
                  onClick={() => setDetailsBill(b)}
                  data-testid={`utility-bill-row-${b.id}`}
                >
                  <TableCell className="whitespace-nowrap">{monthLabel(b.billing_month)} {b.billing_year}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      {b.utility_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{b.service_provider}</TableCell>
                  <TableCell>{b.paid_for_label}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-slate-800">
                    AED {Number(b.amount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {b.paid_through === 'credit_card' ? (
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px]">
                        {b.card_key === 'GEDS_PEMO' ? 'GEDS PEMO' : b.card_key === 'EMS_RAK_BANK' ? 'EMS RAK Bank' : 'Credit Card'}
                      </Badge>
                    ) : b.paid_through === 'petty_cash' ? (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Petty Cash</Badge>
                    ) : b.paid_through === 'bank_transfer' ? (
                      <Badge className="bg-sky-100 text-sky-800 border-sky-300 text-[10px]">Bank Transfer</Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {b.bill_url ? (
                      <a href={b.bill_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 underline text-xs inline-flex items-center gap-1"
                         data-testid={`utility-bill-view-${b.id}`}>
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {b.receipt_url ? (
                      <a href={b.receipt_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 underline text-xs inline-flex items-center gap-1"
                         data-testid={`utility-bill-receipt-${b.id}`}>
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {b.created_by_name || b.created_by_email || <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0"
                          data-testid={`utility-bill-actions-${b.id}`}
                        >
                          <MoreVertical className="w-4 h-4 text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem
                          onClick={() => setEditBill(b)}
                          data-testid={`utility-bill-edit-${b.id}`}
                          className="cursor-pointer"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => deleteBill(b.id)}
                          data-testid={`utility-bill-delete-${b.id}`}
                          className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddUtilityBillDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        options={options}
        onSaved={() => { setAddOpen(false); fetchBills(); }}
      />

      {/* Edit dialog — reuses AddUtilityBillDialog in edit mode */}
      <AddUtilityBillDialog
        open={!!editBill}
        existing={editBill}
        onOpenChange={(v) => !v && setEditBill(null)}
        options={options}
        onSaved={() => { setEditBill(null); fetchBills(); }}
      />

      <UtilityBillDetailsDialog
        bill={detailsBill}
        onClose={() => setDetailsBill(null)}
        onDelete={() => detailsBill && deleteBill(detailsBill.id)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add Utility Bill dialog
// ---------------------------------------------------------------------------

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear - 1, currentYear, currentYear + 1];

function AddUtilityBillDialog({ open, onOpenChange, options, onSaved, existing = null }) {
  const [serviceProvider, setServiceProvider] = useState('');
  const [utilityType, setUtilityType] = useState('');
  const [paidForKey, setPaidForKey] = useState(''); // composite "type::id"
  const [billingMonth, setBillingMonth] = useState(String(new Date().getMonth() + 1));
  const [billingYear, setBillingYear] = useState(String(currentYear));
  const [topupDate, setTopupDate] = useState('');
  const [amount, setAmount] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState(null);
  // NEW: paid-through
  const [paidThrough, setPaidThrough] = useState('');   // '', 'bank_transfer', 'credit_card', 'petty_cash'
  const [cardKey, setCardKey] = useState('');            // GEDS_PEMO | EMS_RAK_BANK
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Duplicate probe — one hard block (invoice_no already exists) + one
  // soft warning (same provider + type + location + month already logged).
  const [dupCheck, setDupCheck] = useState({ invoice_exists: false, invoice_matches: [], soft_duplicates: [] });

  useEffect(() => {
    if (!open) {
      setServiceProvider(''); setUtilityType(''); setPaidForKey('');
      setBillingMonth(String(new Date().getMonth() + 1));
      setBillingYear(String(currentYear));
      setTopupDate('');
      setAmount(''); setRemarks(''); setFile(null);
      setInvoiceNo('');
      setDupCheck({ invoice_exists: false, invoice_matches: [], soft_duplicates: [] });
      setPaidThrough(''); setCardKey(''); setReceiptFile(null);
      return;
    }
    // Prefill when editing an existing bill
    if (existing) {
      setServiceProvider(existing.service_provider || '');
      setUtilityType(existing.utility_type || '');
      setPaidForKey(existing.paid_for_type
        ? `${existing.paid_for_type}::${existing.paid_for_id || ''}`
        : '');
      setBillingMonth(String(existing.billing_month || (new Date().getMonth() + 1)));
      setBillingYear(String(existing.billing_year || currentYear));
      setTopupDate(existing.topup_date || '');
      setAmount(String(existing.amount ?? ''));
      setInvoiceNo(existing.invoice_no || '');
      setRemarks(existing.remarks || '');
      setFile(null);
      setPaidThrough(existing.paid_through || '');
      setCardKey(existing.card_key || '');
      setReceiptFile(null);
    }
  }, [open, existing]);

  // Dependent "Paid For" options based on utilityType
  const paidForChoices = useMemo(() => {
    if (utilityType === 'SALIK Top-up' || serviceProvider === 'SALIK') {
      const locs = (options.swap_locations || []).map(s => ({
        key: `swap_location::${s.id}`, label: s.name, paid_for_type: 'swap_location', paid_for_id: s.id,
      }));
      return [
        { key: 'company::', label: 'Company SALIK Account', paid_for_type: 'company', paid_for_id: null },
        ...locs,
      ];
    }
    if (utilityType === 'Electricity & Water' || utilityType === 'Internet & Landline') {
      const locs = (options.swap_locations || []).map(s => ({
        key: `swap_location::${s.id}`, label: s.name, paid_for_type: 'swap_location', paid_for_id: s.id,
      }));
      return [
        ...locs,
        { key: 'staff_accommodation::', label: 'Staff Accommodation', paid_for_type: 'staff_accommodation', paid_for_id: null },
      ];
    }
    if (utilityType === 'Mobile') {
      const stf = (options.staff || []).map(s => ({
        key: `staff::${s.id}`,
        label: s.name + (s.employee_unique_id ? ` (${s.employee_unique_id})` : ''),
        paid_for_type: 'staff', paid_for_id: s.id,
      }));
      return [
        ...stf,
        { key: 'rider_mobile::', label: 'Rider Mobile', paid_for_type: 'rider_mobile', paid_for_id: null },
      ];
    }
    return [];
  }, [utilityType, options]);

  // Reset paid-for when utility type changes
  useEffect(() => { setPaidForKey(''); }, [utilityType]);

  const selectedPaidFor = paidForChoices.find(p => p.key === paidForKey);

  // Debounced duplicate probe — runs whenever any of the key fields
  // change. Backend returns {invoice_exists, soft_duplicates}. The
  // Save button is disabled while `invoice_exists` is true; the soft
  // warning is a banner the user can dismiss by adjusting the fields.
  useEffect(() => {
    if (!open) return;
    if (!serviceProvider || !utilityType || !selectedPaidFor || !billingMonth || !billingYear) {
      setDupCheck({ invoice_exists: false, invoice_matches: [], soft_duplicates: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          service_provider: serviceProvider,
          utility_type: utilityType,
          paid_for_type: selectedPaidFor.paid_for_type,
          billing_month: billingMonth,
          billing_year: billingYear,
        });
        if (selectedPaidFor.paid_for_id) params.append('paid_for_id', selectedPaidFor.paid_for_id);
        if (invoiceNo.trim()) params.append('invoice_no', invoiceNo.trim());
        if (existing?.id) params.append('exclude_id', existing.id);
        const r = await axios.get(`${API}/utility-bills/duplicate-check?${params}`, getAuth());
        setDupCheck(r.data || { invoice_exists: false, invoice_matches: [], soft_duplicates: [] });
      } catch {
        // Non-fatal — leave the warning off if the probe fails.
        setDupCheck({ invoice_exists: false, invoice_matches: [], soft_duplicates: [] });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [open, serviceProvider, utilityType, paidForKey, billingMonth, billingYear, invoiceNo, existing?.id]);  // eslint-disable-line

  const submit = async () => {
    if (!serviceProvider || !utilityType || !paidForKey || !billingMonth || !billingYear || !amount) {
      toast.error('Please fill all required fields.');
      return;
    }
    if (Number(amount) <= 0) { toast.error('Amount must be greater than zero.'); return; }
    if (paidThrough === 'credit_card' && !cardKey) {
      toast.error('Please select a credit card (GEDS PEMO or EMS RAK Bank).');
      return;
    }
    if (dupCheck.invoice_exists) {
      toast.error('This invoice number is already recorded. Please use a different invoice number.');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('service_provider', serviceProvider);
      fd.append('utility_type', utilityType);
      fd.append('paid_for_type', selectedPaidFor.paid_for_type);
      if (selectedPaidFor.paid_for_id) fd.append('paid_for_id', selectedPaidFor.paid_for_id);
      fd.append('billing_month', billingMonth);
      fd.append('billing_year', billingYear);
      fd.append('amount', amount);
      if (invoiceNo.trim()) fd.append('invoice_no', invoiceNo.trim());
      if (topupDate) fd.append('topup_date', topupDate);
      if (remarks.trim()) fd.append('remarks', remarks.trim());
      if (file) fd.append('bill_file', file);
      if (paidThrough) fd.append('paid_through', paidThrough);
      if (paidThrough === 'credit_card' && cardKey) fd.append('card_key', cardKey);
      if (receiptFile) fd.append('receipt_file', receiptFile);
      if (existing?.id) {
        await axios.put(`${API}/utility-bills/${existing.id}`, fd, {
          headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Utility bill updated');
      } else {
        await axios.post(`${API}/utility-bills`, fd, {
          headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Utility bill added');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="add-utility-bill-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit Utility Bill' : 'Add Utility Bill'}</DialogTitle>
          <DialogDescription>
            {existing ? 'Update the bill details or the payment method.' : 'Record a utility bill paid by the company.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {/* Provider + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Service Provider <span className="text-red-500">*</span></Label>
              <Select value={serviceProvider} onValueChange={setServiceProvider}>
                <SelectTrigger className="h-9" data-testid="ub-provider-select"><SelectValue placeholder="Select provider…" /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Utility Type <span className="text-red-500">*</span></Label>
              <Select value={utilityType} onValueChange={setUtilityType}>
                <SelectTrigger className="h-9" data-testid="ub-utility-type-select"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent>
                  {UTILITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Paid For (dependent) */}
          <div>
            <Label className="text-xs">Paid For <span className="text-red-500">*</span></Label>
            <Select value={paidForKey} onValueChange={setPaidForKey} disabled={!utilityType}>
              <SelectTrigger className="h-9" data-testid="ub-paid-for-select">
                <SelectValue placeholder={utilityType ? 'Select recipient…' : 'Choose utility type first'} />
              </SelectTrigger>
              <SelectContent>
                {paidForChoices.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Billing Month + Year + Amount */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Billing Month <span className="text-red-500">*</span></Label>
              <Select value={billingMonth} onValueChange={setBillingMonth}>
                <SelectTrigger className="h-9" data-testid="ub-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Year <span className="text-red-500">*</span></Label>
              <Select value={billingYear} onValueChange={setBillingYear}>
                <SelectTrigger className="h-9" data-testid="ub-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (AED) <span className="text-red-500">*</span></Label>
              <Input
                type="number" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-9" placeholder="0.00"
                data-testid="ub-amount-input"
              />
            </div>
          </div>

          {/* PDF upload */}
          {(serviceProvider === 'SALIK' || utilityType === 'SALIK Top-up') && (
            <div>
              <Label className="text-xs">Top-up Date <span className="text-red-500">*</span></Label>
              <Input
                type="date" value={topupDate}
                onChange={(e) => setTopupDate(e.target.value)}
                className="h-9"
                data-testid="ub-topup-date"
              />
            </div>
          )}
          <div>
            <Label className="text-xs">Upload PDF Bill (optional)</Label>
            <Input
              type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="h-9"
              data-testid="ub-file-input"
            />
            {file && <p className="text-[10px] text-green-700 mt-1">{file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>

          {/* Invoice No. — hard-dedup on server, live probe on blur */}
          <div>
            <Label className="text-xs">Invoice No. (optional but recommended)</Label>
            <Input
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              className="h-9"
              placeholder="e.g. DEWA-2026-07-4295"
              data-testid="ub-invoice-no-input"
            />
            {dupCheck.invoice_exists && (
              <div className="mt-2 rounded border border-red-300 bg-red-50 text-red-800 text-xs p-2" data-testid="ub-invoice-duplicate-error">
                <strong>Invoice number already recorded.</strong>{' '}
                Match: bill #{dupCheck.invoice_matches[0]?.bill_no} · {dupCheck.invoice_matches[0]?.paid_for_label} · AED {(dupCheck.invoice_matches[0]?.amount || 0).toLocaleString()}. Use a different invoice number.
              </div>
            )}
            {!dupCheck.invoice_exists && dupCheck.soft_duplicates.length > 0 && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs p-2" data-testid="ub-soft-duplicate-warning">
                <strong>Check utility bill date, month, location</strong> — {dupCheck.soft_duplicates.length} existing entr{dupCheck.soft_duplicates.length === 1 ? 'y' : 'ies'} for the same location, utility provider and month:
                <ul className="mt-1 list-disc pl-4 space-y-0.5">
                  {dupCheck.soft_duplicates.slice(0, 3).map(d => (
                    <li key={d.id}>#{d.bill_no} · AED {(d.amount || 0).toLocaleString()}{d.invoice_no ? ` · Inv ${d.invoice_no}` : ''}{d.created_by_name ? ` · by ${d.created_by_name}` : ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Paid Through */}
          <div className="border-t pt-3 space-y-2">
            <Label className="text-xs">Paid Through</Label>
            <select
              value={paidThrough}
              onChange={(e) => { setPaidThrough(e.target.value); if (e.target.value !== 'credit_card') setCardKey(''); }}
              className="h-9 w-full border border-slate-200 rounded px-2 text-sm bg-white"
              data-testid="ub-paid-through"
            >
              <option value="">— select payment method —</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="credit_card">Paid from Credit Card</option>
              <option value="petty_cash">Paid from Petty Cash</option>
            </select>
            {paidThrough === 'credit_card' && (
              <select
                value={cardKey}
                onChange={(e) => setCardKey(e.target.value)}
                className="h-9 w-full border border-slate-200 rounded px-2 text-sm bg-white"
                data-testid="ub-card-key"
              >
                <option value="">— select card —</option>
                <option value="GEDS_PEMO">GEDS PEMO</option>
                <option value="EMS_RAK_BANK">EMS RAK Bank</option>
              </select>
            )}
            {paidThrough && (
              <div>
                <Label className="text-[11px] text-slate-600">Upload Receipt (payment proof)</Label>
                <Input
                  type="file" accept="application/pdf,image/*"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="h-9"
                  data-testid="ub-receipt-input"
                />
                {receiptFile && <p className="text-[10px] text-green-700 mt-1">{receiptFile.name} ({Math.round(receiptFile.size / 1024)} KB)</p>}
              </div>
            )}
            {paidThrough === 'credit_card' && (
              <p className="text-[10px] text-indigo-700">Auto-adds an entry to Credit Card Expenses log.</p>
            )}
            {paidThrough === 'petty_cash' && (
              <p className="text-[10px] text-amber-700">Auto-adds a Vendor Expense to Petty Cash (deducts drawer balance).</p>
            )}
          </div>

          {/* Remarks */}
          <div>
            <Label className="text-xs">Remarks (optional)</Label>
            <Textarea
              value={remarks} onChange={(e) => setRemarks(e.target.value)}
              rows={2} placeholder="Internal notes…"
              data-testid="ub-remarks-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="ub-save-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {existing ? 'Save Changes' : 'Save Bill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Details dialog (read-only)
// ---------------------------------------------------------------------------

const BillField = ({ label, value, mono, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</p>
    <p className={`${mono ? 'font-mono' : ''} text-sm ${!value ? 'text-slate-400' : 'text-slate-800'}`}>
      {value || '—'}
    </p>
  </div>
);

function UtilityBillDetailsDialog({ bill, onClose, onDelete }) {
  if (!bill) return null;
  return (
    <Dialog open={!!bill} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="utility-bill-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-mono">{bill.bill_no}</span>
          </DialogTitle>
          <DialogDescription>
            {bill.service_provider} · {bill.utility_type} · {monthLabel(bill.billing_month)} {bill.billing_year}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 py-2">
          <BillField label="Service Provider" value={bill.service_provider} />
          <BillField label="Utility Type" value={bill.utility_type} />
          <BillField label="Paid For" value={bill.paid_for_label} full />
          <BillField label="Billing Month" value={monthLabel(bill.billing_month)} />
          <BillField label="Year" value={bill.billing_year} />
          <BillField label="Amount" value={`AED ${Number(bill.amount || 0).toLocaleString()}`} />
          <BillField label="Top-up Date" value={bill.topup_date} />
          <BillField label="Recorded On" value={bill.created_at ? new Date(bill.created_at).toLocaleString() : '—'} />
          <BillField label="Remarks" value={bill.remarks} full />
        </div>
        <div className="border-t pt-3 flex items-center justify-between text-xs">
          <span className="text-slate-500">PDF Bill:</span>
          {bill.bill_url ? (
            <a href={bill.bill_url} target="_blank" rel="noreferrer"
               className="text-blue-600 underline inline-flex items-center gap-1"
               data-testid="utility-bill-details-pdf">
              <ExternalLink className="w-3 h-3" /> Open in new tab
            </a>
          ) : <span className="text-slate-400">No file attached</span>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="destructive" onClick={onDelete} data-testid="utility-bill-details-delete">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UtilityBills;
